import {makeStyles} from "@material-ui/core/styles"
import React, {useEffect, useMemo, useRef, useState} from "react"
import {withHistory} from "slate-history";
import {Slate, withReact} from "slate-react"
import {createEditor} from "slate"
import {Box, FormControl, FormHelperText, useTheme} from "@material-ui/core"
import clsx from "clsx"
import Toolbar from "./Toolbar"
import RichTextEditor from "../RichText/RichTextEditor"
import * as PropTypes from "prop-types"
import lang from "../../core/lang";
import useDimension from "../../hook/useDimension";
import UserEvent from "../../core/UserEvent";
import RichTextCanvas from "../RichText/RichTextCanvas";
import _ from "underscore";

const EMPTY = {
    type: 'rtx',
    children: [
        {
            type: 'paragraph',
            children: [{ text: '' }]
        }
    ]
};

const parse = any => {
    if(lang.isEmpty(any)) {
        return EMPTY
    }

    if(lang.isObject(any)) {
        return any
    }

    let value;
    let isValid = false;
    try {
        if(lang.isString(any) && isNaN(parseFloat(any))) {
            value = JSON.parse(any);
            isValid = true;
        }
    } catch (e) {
        isValid = false;
    }

    if(!isValid) {
        const text = String(any)
        const lines = text.split('\n')
        const children = lines.map(line => ({
            type: 'paragraph',
            children: [{ text: line }],
        }));
        value = {
            type: 'rtx',
            children
        };
    }

    return value
}

const stringify = value => {
    if(lang.isString(value)) {
        return value
    }
    if(lang.isObject(value)) {
        return JSON.stringify(value)
    } else {
        return String(value)
    }
}

const useStyles = makeStyles(theme => ({
    root: {
        cursor: props => props.disabled ? 'not-allowed' : 'auto',
        height: props => props.height,
        width: props => props.width,
        fontSize: '1rem'
    },
    input: {
        borderStyle: 'solid',
        borderRadius: theme.shape.borderRadius,
        borderWidth: '2px',
        borderColor: props => props.error ? theme.palette.error.main :"rgba(0, 0, 0, 0.23)",
        transition: 'border-color 0.3s ease',
        '&:hover, &:has(*:focus)': {
            borderColor: theme.palette.primary.main,
        },
        height: '100%',
        width: '100%'
    },
    toolbar: {
        borderBottomStyle: 'solid',
        borderBottomWidth: '2px',
        borderBottomColor: 'rgba(0, 0, 0, 0.23)'
    },
    canvas: {
        height: '100%',
        overflowY: 'auto'
    }
}))

function RichTextField(props) {
    const {value, defaultValue, className, error, fullHeight, fullWidth, autofocus, helperText, placeholder, required, disabled, readOnly, commands, mode} = props
    const isControlled = value !== undefined
    // Create a Slate editor object that won't change across renders.
    const editor = useMemo(() => RichTextEditor.withInlines(withHistory(withReact(createEditor()))), [])
    const dimension = useDimension()
    const theme = useTheme()
    const defaultHeight = dimension.formControlHeight() * 5 + theme.spacing(2)
    const width = fullWidth ? '100%' : props.width || '100%'
    const height = fullHeight ? '100%' : props.height || defaultHeight
    const classes = useStyles({disabled, height, width, error})
    const [initialized, setInitialized] = useState(false);
    const style = {...props.style};
    const isUpdatingProgrammatically = useRef(false) // 用於判斷是否為程式碼觸發的變更

    const handleChange = value => {
        // 檢查是否為程式碼觸發的更新
        if (isUpdatingProgrammatically.current) {
            // 重設旗標，並退出，不執行props.onChange，以避免造成無窮迴圈
            isUpdatingProgrammatically.current = false
            return
        }
        const content = {
            type: 'rtx',
            children: value
        }
        props.onChange && props.onChange(new UserEvent({value: content}))
    }

    useEffect(() => {
        setInitialized(true)
    }, [])

    useEffect(() => {
        if (!isControlled || !initialized) {
            return
        }

        const rtx = parse(value)
        if (!_.isEqual(rtx.children, editor.children)) {
            // 在執行變更操作前，設定旗標
            isUpdatingProgrammatically.current = true
            RichTextEditor.reset(editor, rtx.children)
        }
    }, [value, editor, isControlled, initialized])

    const initialValue = parse(isControlled ? value : defaultValue)

    if(readOnly) {
        style.pointerEvents = '';
    }

    return (
        <FormControl className={clsx('appfuse-RichTextField', className, classes.root)} style={style} focused={autofocus}
                     required={required} disabled={disabled} variant="outlined" >
            <Slate editor={editor} initialValue={initialValue.children} onChange={handleChange}>
                <Box className={classes.input} display="flex" flexDirection="column">
                    {disabled || readOnly ? null : <Toolbar className={classes.toolbar} commands={commands} mode={mode} />}
                    <Box className={classes.canvas} padding={1} >
                        <RichTextCanvas readOnly={readOnly} disabled={disabled} placeholder={placeholder} autofocus={autofocus} />
                    </Box>
                </Box>
            </Slate>
            {disabled || readOnly || lang.isEmpty(helperText) ? null : <FormHelperText required={required} >{helperText}</FormHelperText>}
        </FormControl>
    )
}

RichTextField.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func,
    name: PropTypes.string,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    height: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    helperText: PropTypes.string,
    autofocus: PropTypes.bool,
    mode: PropTypes.string,
    error: PropTypes.bool,
}

RichTextField.defaultProps = {
    placeholder: "Enter here ......"
}

export default RichTextField